/* eslint-disable max-len */

import apiRoutes from 'config/apiRoute';
import { all, call, put, select, StrictEffect, takeEvery, takeLatest } from 'redux-saga/effects';
import api from 'utils/api';
import { AxiosResponse } from 'utils/interface';
import { RootState } from 'store';
import { buildAugmentedUsers, mapTimerToActiveLog } from 'components/Layout/components/UserDropdown/avatarSheet.utils';

import {
  fetchTeamActivity,
  setTeamActivityLoading,
  setTeamActivityRevalidating,
  setTeamActivitySuccess,
  setTeamActivityError,
  fetchAndUpdateUserActiveLog,
  setUserTimelogs,
} from './teamActivity-slice';

const { get } = api;

/**
 * worker saga
 */
export function* fetchTeamActivitySagaListener(): any {
  // Guard: if already loading, skip
  const isLoading = yield select((store) => store.teamActivity.loading);
  if (isLoading) {
    return;
  }

  try {
    const hasUsers = yield select((store: RootState) => store.teamActivity.users.length > 0);

    if (hasUsers) {
      yield put(setTeamActivityRevalidating());
    } else {
      yield put(setTeamActivityLoading());
    }

    const currentUser = yield select((store: RootState) => store.auth.user);
    const currentUserUuid = currentUser?.uuid;

    const [contactsRes, timeLogsRes]: AxiosResponse[] = yield all([
      call(get, apiRoutes.CONTACTS, {
        'filters[type][]': 'regular_user',
        'filters[billing_contacts.status][]': 'active',
        'page[size]': 999,
      }),
      call(get, apiRoutes.TIMERS, { 'page[size]': 500 }),
    ]);

    const contacts = contactsRes.data.data || [];
    const allUsers = contacts.map((contact: any) => contact.user).filter((user: any) => user);
    const rawUsers = allUsers.filter((user: any) => user.uuid !== currentUserUuid);
    const allLogs = (timeLogsRes.data.data || []).map(mapTimerToActiveLog);
    const timeLogs = allLogs.filter((log: any) => log.status === 'running');
    const pausedLogs = allLogs.filter((log: any) => log.status === 'paused');

    const augmentedUsers = buildAugmentedUsers(rawUsers, timeLogs, pausedLogs);

    const currentUserRaw = allUsers.find((user: any) => user?.uuid === currentUserUuid);
    const currentUserTimeLog = currentUserRaw
      ? timeLogs.find((log: any) => log.user?.uuid === currentUserRaw.uuid)
      : null;
    const currentUserPausedLogs = currentUserRaw
      ? pausedLogs.filter((log: any) => log.user?.uuid === currentUserRaw.uuid)
      : [];

    const currentUserData = currentUserRaw ? {
      ...currentUserRaw,
      activeLog: currentUserTimeLog || null,
      pausedLogs: currentUserPausedLogs.length > 0 ? currentUserPausedLogs : undefined,
    } : null;

    yield put(setTeamActivitySuccess({
      users: augmentedUsers,
      currentUserData,
    }));
  } catch (e) {
    yield put(setTeamActivityError());
  }
}

/**
 * worker saga: fetch running and paused timelogs for a single user and update store
 */
export function* fetchUserActiveLogSagaListener(action: any): any {
  const { userId } = action.payload;
  try {
    const res: AxiosResponse = yield call(get, apiRoutes.TIMERS, {
      'filter[user]': userId,
      'page[size]': 500,
    });
    const logs = (res.data.data || []).map(mapTimerToActiveLog);
    const activeLog = logs.find((l: any) => l.status === 'running') ?? null;
    const pausedLogs = logs.filter((l: any) => l.status === 'paused');
    yield put(setUserTimelogs({ userId, activeLog, pausedLogs: pausedLogs.length > 0 ? pausedLogs : undefined }));
  } catch {
    // silently fail; stale data is acceptable here
  }
}

/**
 * watcher saga
 */
function* teamActivitySaga(): Generator<StrictEffect> {
  yield takeLatest(fetchTeamActivity.type, fetchTeamActivitySagaListener);
  yield takeEvery(fetchAndUpdateUserActiveLog.type, fetchUserActiveLogSagaListener);
}

export default teamActivitySaga;
